import { useContext } from 'react'
import { OptimizelyContext } from '@optimizely/react-sdk'
import {
  OptimizelyGetParam,
  OptimizelyPushParams,
  UseOptimizelyResponse
} from './useOptimizely.types'

/**
 * Custom hook for accessing Optimizely methods.
 *
 * @example
 * // Usage of the `get` method
 * const { optimizely } = useOptimizely();
 * optimizely.get('yourAttributeName');
 *
 * @example
 * // Usage of the `push` method
 * const { optimizely } = useOptimizely();
 * optimizely.push({ /* your parameters * / });
 *
 * @see {@link https://docs.developers.optimizely.com/web-experimentation/reference/get}
 * Documentation for the `get` method.
 *
 * @see {@link https://docs.developers.optimizely.com/web-experimentation/reference/push}
 * Documentation for the `push` method.
 */
const useOptimizely = (): UseOptimizelyResponse => {
  const { optimizely: optimizelySdk } = useContext(OptimizelyContext)

  return {
    optimizely: {
      get: (attributeName: OptimizelyGetParam) => {
        if (!window.optimizely?.get) return

        return window.optimizely.get(attributeName)
      },
      push: (params: OptimizelyPushParams) => {
        if (!window.optimizely?.push) return

        return window.optimizely.push(params)
      },
      track: (eventName: string) => {
        if (!optimizelySdk?.track) return

        optimizelySdk.track(eventName)
      }
    }
  }
}

export default useOptimizely
